/* ═══════════════════════════════════════════════════════════════════════════
   MAISON MERCIER — LE CŒUR DE LA WISHLIST  ·  mm-wishlist.js
   ═══════════════════════════════════════════════════════════════════════════
   ⚠️ Chargé par `layout/theme.liquid` avec `defer`, **juste avant `</body>`**.

   ── 🎯 ÉTAPE 2 — LE SERVEUR EST DEVENU LA SOURCE DE VÉRITÉ ────────────────
   L'étape 1 gardait tout dans le navigateur. **L'étape 2 branche le cœur sur
   l'App Proxy** — *le serveur est déployé, signé, et mesuré.*

   | | étape 1 | **étape 2 (ce fichier)** |
   |---|---|---|
   | apparence, états, accessibilité | ✅ | **inchangés** — *aucune ligne touchée* |
   | survit au rechargement | ✅ | ✅ |
   | suit le client d'un appareil à l'autre | ⛔ | ✅ **serveur** |
   | anonyme → connexion → ajout auto | ⛔ | ✅ **§9-§12** |
   | requêtes par page | 0 | **1 maximum** |

   ── 🔑 LA SEULE REQUÊTE, ET QUAND ELLE N'A PAS LIEU D'ÊTRE ────────────────
   Le brief demande **UNE requête par page**. Ici il y en a **au plus une**,
   et **zéro pour la grande majorité des visiteuses** :

   ```
     visiteur ANONYME  (data-mm-client="0")  →  0 requête
     CLIENT connecté   (data-mm-client="1")  →  1 requête  (GET, la liste)
     attribut ABSENT   (thème non repatché)  →  1 requête  (repli sûr)
   ```

   ⛔ **LE REPLI EST LE POINT IMPORTANT :** *l'indication vient de `{% if customer %}`
   dans le layout.* **Si elle manque — thème pas encore mis à jour, mise en cache —
   le script FAIT la requête au lieu de supposer « pas connecté ».** *Supposer
   aurait rendu une cliente connectée anonyme à ses propres yeux : elle aurait vu
   ses favoris disparaître.*
   🔑 **L'optimisation est un bonus ; l'exactitude est le défaut.**

   ── ⛔⛔ §16 — CE QU'IL N'Y A PAS DANS CE FICHIER, ET POURQUOI ──────────────
   **Aucun identifiant de client n'est lu, calculé, stocké ni envoyé.**
   *Pas de `customer_id`, pas de jeton, pas de cookie.*
   ➜ **L'identité vient du paramètre SIGNÉ que Shopify ajoute à l'App Proxy.**
   **Le navigateur n'a aucun moyen d'influer dessus** — *et c'est exactement ce
   que §16 exige.*

   ── 🗂 LES DEUX MÉMOIRES, ET CE QUE CHACUNE SERT ──────────────────────────
   ```
     mm_favoris_v2       (localStorage)    l'état complet : liste + ce qui reste à pousser
     mm_pending_wishlist (sessionStorage)  §9-§12 : ce qui a été coché SANS COMPTE
   ```
   ⚠️ **DEUX MÉMOIRES PARCE QU'ELLES N'ONT PAS LA MÊME DURÉE DE VIE.**
   *`localStorage` est la mémoire de la visiteuse — elle survit à la fermeture du
   navigateur.* **`sessionStorage` est celle du PARCOURS** : *elle n'a de sens
   que le temps de l'aller-retour vers la connexion, et elle disparaît ensuite.*
   ➜ *Les confondre laisserait une file d'attente derrière chaque cliente à jamais.*

   ── ⚠️ POURQUOI CE FICHIER EST EN `var`, SANS `let`, `const` NI FLÈCHES ───
   *Ce code part chez de vraies clientes, sur des navigateurs qu'on ne choisit
   pas.* **Une syntaxe non reconnue ne dégrade pas : elle empêche TOUT le fichier
   de s'exécuter** — *et le symptôme serait « les cœurs ont disparu », sans
   erreur visible.* ➜ **ES5, et pas de transpileur.**
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
    "use strict";

    /* ── LES NOMS, TOUS AU MÊME ENDROIT ────────────────────────────────────
       🔑 **La version est dans le nom de la clé.** *Le jour où le format change,
       l'ancien contenu n'est pas relu comme s'il était le nouveau.* */
    var VERSION = "v2";
    var CLE = "mm_favoris_" + VERSION;
    var CLE_V1 = "mm_favoris_v1";
    var CLE_SESSION = "mm_pending_wishlist";
    var CLASSE_HOTE = "mm-coeur-hote";
    var SELECTEUR_ANCRE = "li[data-product-id]";

    /** *Les trois routes — *et elles passent TOUTES par le même proxy.* */
    var BASE = "/apps/wishlist/wishlist";

    /** *Le plafond de la file d'attente.*
     *  ⛔ **Sans borne, une cliente qui coche 300 produits pendant une panne
     *     réseau pousserait 300 requêtes au retour.** *Le plafond n'est pas une
     *     limite d'usage : c'est une limite de rafale.* */
    var MAX_ATTENTE = 60;

    /* ⛔ LE TRACÉ, EN MONOLINE. *Un seul chemin : la même forme que la planche
       `scratch/wishlist/APERCU-DU-COEUR.html`, pour qu'il n'y ait pas deux
       dessins différents entre l'aperçu et le site.* */
    var SVG =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="M12 20.25S3.75 15 3.75 9.4A4.15 4.15 0 0 1 12 7.1a4.15 4.15 0 0 1 8.25 2.3C20.25 15 12 20.25 12 20.25Z"' +
        ' fill="none" stroke="currentColor" stroke-width="1.5"' +
        ' stroke-linecap="round" stroke-linejoin="round"/>' +
        "</svg>";

    /* ══════════════════════════════════════════════════════════════════════
       ⏱ L'INDICATION DE CONNEXION — *lue UNE FOIS, tout en haut, exprès.*
       ⛔ **`document.currentScript` N'EST VALIDE QUE PENDANT L'EXÉCUTION DU
         SCRIPT.** *`demarrer()` s'exécute bien plus tard, sur `DOMContentLoaded`
         — à ce moment-là, `document.currentScript` vaut `null`, et la lecture
         rendrait `null` **en ayant l'air de marcher**.*
       ➜ **D'où la capture ici, synchrone, avant tout le reste.** */
    var HINT = null; /* null = inconnu · true = connecté · false = anonyme */
    try {
        var cs = document.currentScript;
        if (cs && cs.getAttribute) {
            var h = cs.getAttribute("data-mm-client");
            if (h === "1") HINT = true;
            else if (h === "0") HINT = false;
        }
    } catch (e) { /* pas de `currentScript` — on restera sur le repli sûr */ }

    /* ══════════════════════════════════════════════════════════════════════
       LE STOCKAGE — *et les trois façons dont il peut échouer.*
       ⛔ `localStorage` LÈVE une exception (pas un `null`) en navigation privée
       sur certains navigateurs, et quand le quota est plein. **Un cœur qui
       plante parce que le stockage est indisponible est pire que pas de cœur** :
       *il emporte le reste du script avec lui.*
       ➜ D'où les `try/catch` **à chaque accès**, et pas seulement à l'ouverture.
       🔑 **Et une sonde d'ÉCRITURE, pas seulement de présence :** *Safari en
       navigation privée expose `localStorage` et refuse l'écriture.* **Un test
       de présence aurait dit « disponible ».**
       ══════════════════════════════════════════════════════════════════════ */
    var MAGASIN = null, MAGASIN_TESTE = false;

    function magasin() {
        if (MAGASIN_TESTE) return MAGASIN;
        MAGASIN_TESTE = true;
        var essais = [];
        try { essais.push(window.localStorage); } catch (e) { /* refusé */ }
        try { essais.push(window.sessionStorage); } catch (e) { /* refusé */ }
        for (var i = 0; i < essais.length; i++) {
            var m = essais[i];
            if (!m) continue;
            try {
                m.setItem("mm_sonde", "1");
                m.removeItem("mm_sonde");
                MAGASIN = m; /* ✅ écrit ET relu : c'est un vrai stockage */
                return MAGASIN;
            } catch (e2) { /* présent mais inutilisable — on essaie le suivant */ }
        }
        return null; /* ⚠️ Aucune mémoire : le cœur marchera, il oubliera. */
    }

    function lireBrut(cle) {
        var m = magasin();
        if (!m) return null;
        try { return m.getItem(cle); } catch (e) { return null; }
    }

    function ecrireBrut(cle, texte) {
        var m = magasin();
        if (!m) return false;
        try {
            if (texte === null) m.removeItem(cle);
            else m.setItem(cle, texte);
            return true;
        } catch (e) {
            /* ⛔ QUOTA PLEIN. *Un échec silencieux ici ferait croire à une
               mémoire qui n'a rien retenu* — *on rend `false`, l'appelant
               prévient à l'écran.* */
            return false;
        }
    }

    /* ══════════════════════════════════════════════════════════════════════
       L'ÉTAT — *un seul objet, et une seule forme.*
       ```
         liste      ce que l'INTERFACE lit        — le miroir local
         aAjouter   ce qui reste à POUSSER (ajout)
         aRetirer   ce qui reste à POUSSER (retrait)
         sync       horodatage du dernier accord avec le serveur (0 = jamais)
       ```
       🔑 **POURQUOI `aAjouter`/`aRetirer` EXISTENT SÉPARÉMENT DE `liste`.**
       *Si on poussait simplement « l'état local » au serveur, on écraserait ce
       qui a été fait sur un AUTRE APPAREIL.*
       ➜ **Seule la file d'attente est poussée ; le reste vient du serveur.**
       *C'est ce qui rend le retrait sur un appareil B visible sur l'appareil A
       — sans quoi le favori ressusciterait.* */
    function etatVide() {
        return { version: 2, liste: [], aAjouter: [], aRetirer: [], sync: 0 };
    }

    function normaliser(id) {
        var s = String(id === undefined || id === null ? "" : id);
        var i = s.lastIndexOf("/");
        return i === -1 ? s : s.slice(i + 1);
    }

    function listePropre(v) {
        if (Object.prototype.toString.call(v) !== "[object Array]") return [];
        var out = [], vus = {};
        for (var i = 0; i < v.length; i++) {
            var id = normaliser(v[i]);
            if (!id || vus[id]) continue;
            vus[id] = 1;
            out.push(id);
        }
        return out;
    }

    /* ── LA MIGRATION DEPUIS L'ÉTAPE 1 ─────────────────────────────────────
       ⛔ **UNE ANCIENNE LISTE DE FAVORIS N'EST PAS UNE LISTE DE FAVORIS SERVEUR.**
       *Les entrées de la v1 ont été cochées **sans compte** — elles n'ont jamais
       été poussées.* ➜ **Elles deviennent la file d'attente d'AJOUT**, pas la
       liste : *la prochaine connexion les enverra.*
       🔑 *Les verser dans `liste` les ferait passer pour des favoris confirmés,
       et le premier accord avec le serveur les effacerait — **la cliente verrait
       ses cœurs disparaître au moment précis où elle se connecte.*** */
    function migrerV1() {
        var brut = lireBrut(CLE_V1);
        if (!brut) return null;
        var anciens;
        try { anciens = JSON.parse(brut); } catch (e) { return null; }
        if (Object.prototype.toString.call(anciens) !== "[object Array]") return null;
        var ids = listePropre(anciens);
        if (!ids.length) return null;
        var e = etatVide();
        e.liste = ids.slice(0);
        e.aAjouter = ids.slice(0);
        return e;
    }

    function lireEtat() {
        var brut = lireBrut(CLE);
        if (!brut) {
            var migre = migrerV1();
            return migre || etatVide();
        }
        var v;
        try { v = JSON.parse(brut); } catch (e) { return etatVide(); }
        /* ⛔ **UNE FORME INATTENDUE N'EST PAS UNE RAISON DE PLANTER.** *Si un
           autre script a écrit dans cette clé, on repart d'un état vide — et
           l'interface reste cohérente au lieu de jeter.* */
        if (!v || typeof v !== "object" || Object.prototype.toString.call(v) === "[object Array]") {
            return etatVide();
        }
        return {
            version: 2,
            liste: listePropre(v.liste),
            aAjouter: listePropre(v.aAjouter).slice(0, MAX_ATTENTE),
            aRetirer: listePropre(v.aRetirer).slice(0, MAX_ATTENTE),
            sync: typeof v.sync === "number" ? v.sync : 0,
        };
    }

    function ecrireEtat(e) {
        var ok = ecrireBrut(CLE, JSON.stringify(e));
        /* ── ⛔ LA FILE DE SESSION EST ÉCRITE **EN PLUS**, ET CE N'EST PAS UN DOUBLON ──
           §9-§12 : *le favori coché sans compte doit survivre à l'aller-retour
           vers la connexion.* **`sessionStorage` est la mémoire de CE parcours** ;
           *si `localStorage` est indisponible (navigation privée), c'est elle qui
           porte tout.* ➜ *Deux écritures, deux durées de vie, une seule vérité.*
           ⛔⛔ **ET ELLE VA DANS `sessionStorage`, PAS DANS `magasin()`** — *c'est
           un défaut qui a été LIVRÉ, puis attrapé par le banc `33-`.*
           **`magasin()` rend UN SEUL stockage (le premier utilisable) et sert
           `CLE`.** *L'utiliser pour `CLE_SESSION` rangeait la file de parcours
           dans `localStorage` :* **elle aurait survécu à la fermeture du
           navigateur, et une file d'attente serait restée derrière chaque
           cliente à jamais.** *Exactement ce que la séparation des deux clés
           devait empêcher.* 🔑 *Le banc l'a vu sur un `null` — pas sur une
           erreur.* */
        ecrireSession(JSON.stringify(e.aAjouter));
        return ok;
    }

    /** *Écrit la file de parcours là où elle doit vivre : `sessionStorage`.*
     *  ⚠️ **Et si `sessionStorage` est refusé — navigation privée ancienne,
     *     quota — on retombe sur le magasin principal au lieu de perdre la
     *     file.** *Perdre la file, c'est perdre les favoris cochés avant la
     *     connexion : c'est le seul cas où la dégradation coûte quelque chose.* */
    function ecrireSession(texte) {
        try {
            window.sessionStorage.setItem(CLE_SESSION, texte);
            return true;
        } catch (e) {
            return ecrireBrut(CLE_SESSION, texte);
        }
    }

    function estFavori(id) {
        return lireEtat().liste.indexOf(normaliser(id)) !== -1;
    }

    /* ══════════════════════════════════════════════════════════════════════
       L'ÉTAT VISUEL — *une seule fonction, et c'est la seule qui écrit l'état.*
       🔑 **`aria-pressed` ET `aria-label` sont posés ENSEMBLE.** *Un bouton dont
       l'apparence dit « favori » et dont l'annonce dit « ajouter » est un
       mensonge pour qui écoute la page. Les séparer, c'est garantir qu'ils
       divergeront un jour.*
       ══════════════════════════════════════════════════════════════════════ */
    function poserEtat(bouton, actif, titre) {
        bouton.setAttribute("aria-pressed", actif ? "true" : "false");
        var quoi = titre ? " " + titre : "";
        bouton.setAttribute("aria-label", actif
            ? "Retirer" + quoi + " des favoris"
            : "Ajouter" + quoi + " aux favoris");
        bouton.setAttribute("title", actif ? "Retirer des favoris" : "Ajouter aux favoris");
    }

    /* ══════════════════════════════════════════════════════════════════════
       LE BATTEMENT — 1 → 1,15 → 1, **UNE SEULE FOIS**.
       ⛔ Le défaut classique : un double-clic rapide relance l'animation et le
       cœur « tremble ». ➜ **Le garde-fou est par bouton, pas global** —
       *cliquer deux cartes différentes ne doit pas bloquer la seconde.*
       ══════════════════════════════════════════════════════════════════════ */
    function battre(bouton) {
        if (bouton.getAttribute("data-mm-bat") === "1") return;
        bouton.setAttribute("data-mm-bat", "1");
        bouton.classList.add("mm-coeur--clic");
        /* ⛔ `animationend` PEUT NE JAMAIS VENIR : *mouvement réduit supprime
           l'animation, et un onglet en arrière-plan ne la joue pas.* **Sans le
           délai de secours, le bouton resterait bloqué et ne rebattrait
           jamais.** */
        var fini = function () {
            bouton.classList.remove("mm-coeur--clic");
            bouton.removeAttribute("data-mm-bat");
        };
        bouton.addEventListener("animationend", fini, { once: true });
        window.setTimeout(function () {
            if (bouton.getAttribute("data-mm-bat") === "1") fini();
        }, 420);
    }

    /* ══════════════════════════════════════════════════════════════════════
       ⛔⛔ LE RÉSEAU — *et il ne lève JAMAIS.*
       ══════════════════════════════════════════════════════════════════════
       🔑 **Trois issues, et chacune a un sens différent :**
       ```
         { ok: true,  connecte: true  }   le serveur a répondu, la cliente est identifiée
         { ok: true,  connecte: false }   le serveur a répondu 401 : ANONYME (normal)
         { ok: false, connecte: null  }   on n'a PAS pu savoir → on ne touche à rien
       ```
       ⛔ **LA TROISIÈME EST CELLE QUI COMPTE.** *Une panne réseau, un 502, du HTML
       au lieu de JSON — tout ça doit laisser l'état local INTACT.*
       **Un `catch` qui vide la liste parce qu'il n'a pas compris la réponse
       effacerait les favoris d'une cliente à cause d'un wifi instable.**
       ➜ **On échoue en gardant — jamais en effaçant.** */
    var REQUETES = 0;

    function appeler(chemin, methode, corps) {
        REQUETES += 1;
        var options = {
            method: methode || "GET",
            /* ⚠️ `same-origin` EXPLICITE : *c'est le cookie de session Shopify qui
               permet au proxy de savoir QUI appelle.* **Sans lui, tout le monde
               serait anonyme.*** */
            credentials: "same-origin",
            headers: { "Accept": "application/json" },
        };
        if (corps) {
            options.headers["Content-Type"] = "application/json";
            options.body = JSON.stringify(corps);
        }

        return fetch(BASE + (chemin || ""), options).then(function (r) {
            /* ⛔ **401 N'EST PAS UNE ERREUR : C'EST UNE RÉPONSE.** *C'est la façon
               dont le serveur dit « pas connecté » — et c'est le cas NORMAL de la
               grande majorité des visiteuses.* ➜ *On le traduit en `connecte:false`
               au lieu de le jeter avec les vraies pannes.* */
            if (r.status === 401) return { ok: true, connecte: false, statut: 401 };

            if (!r.ok) return { ok: false, connecte: null, statut: r.status };

            return r.json().then(function (d) {
                return { ok: true, connecte: true, statut: r.status, donnees: d };
            }, function () {
                /* *Un corps non-JSON sur un 200 : le serveur a rendu autre chose
                   que ce qu'on attend. On ne l'interprète pas.* */
                return { ok: false, connecte: null, statut: r.status };
            });
        }, function () {
            /* *Réseau coupé, DNS, CORS, onglet fermé. Rien n'a été appris.* */
            return { ok: false, connecte: null, statut: 0 };
        });
    }

    var RAISON_SYNC = "";

    /* ══════════════════════════════════════════════════════════════════════
       🚀 LA SYNCHRONISATION — *le seul endroit qui fait autorité.*
       ══════════════════════════════════════════════════════════════════════
       Elle fait **quatre choses, dans cet ordre, et l'ordre est le sujet :**
       ```
         ① lire la liste du SERVEUR          — avant de pousser quoi que ce soit
         ② pousser la FILE D'ATTENTE         — les ajouts, puis les retraits
         ③ composer : serveur ∪ ajouts \ retraits
         ④ vider la file et l'écrire
       ```
       ⛔ **POURQUOI ① AVANT ② :** *si on poussait d'abord, on ne saurait plus
       distinguer « le serveur ne l'avait pas » de « je viens de l'y mettre » —
       et on perdrait le compte de ce qui restait à faire.* */
    var enCours = false;

    function synchroniser() {
        if (enCours) return Promise.resolve({ ok: false, raison: "DEJA_EN_COURS" });
        enCours = true;

        var e = lireEtat();

        return appeler("", "GET", null).then(function (r) {
            /* ⛔ **ON N'A RIEN APPRIS → ON NE TOUCHE À RIEN.** *Pas de vidage de
               file, pas de remplacement de liste. L'état local reste la vérité
               jusqu'à la prochaine occasion.* */
            if (!r.ok) {
                RAISON_SYNC = "INDETERMINE";
                enCours = false;
                return { ok: false, raison: "INDETERMINE" };
            }

            /* ── ANONYME — *et ce n'est pas un échec.* ───────────────────────
               *La file d'attente reste : c'est elle qui partira à la connexion.* */
            if (!r.connecte) {
                HINT = false;
                e.sync = 0;
                ecrireEtat(e);
                RAISON_SYNC = "ANONYME";
                enCours = false;
                return { ok: true, connecte: false };
            }

            HINT = true;
            var duServeur = [];
            var favoris = (r.donnees && r.donnees.favoris) || [];
            for (var i = 0; i < favoris.length; i++) {
                var id = normaliser(favoris[i] && favoris[i].produit);
                if (id) duServeur.push(id);
            }

            /* ── ② POUSSER LA FILE — *en série, et c'est délibéré.* ──────────
               ⛔ **En parallèle, la borne de débit du Worker deviendrait une
                 devinette.** *En série, la file de 60 rend au pire 60 appels
                 espacés par la latence — et le serveur ne voit jamais une
                 rafale qu'il doit refuser.* */
            var aPousser = [];
            for (var a = 0; a < e.aAjouter.length; a++) {
                if (duServeur.indexOf(e.aAjouter[a]) === -1) {
                    aPousser.push(["", "POST", { produit: e.aAjouter[a] }]);
                }
            }
            for (var b = 0; b < e.aRetirer.length; b++) {
                if (duServeur.indexOf(e.aRetirer[b]) !== -1) {
                    aPousser.push(["/remove", "POST", { produit: e.aRetirer[b] }]);
                }
            }

            var echoues = [];
            var chaine = Promise.resolve();
            for (var c = 0; c < aPousser.length; c++) {
                (function (appel) {
                    chaine = chaine.then(function () {
                        return appeler(appel[0], appel[1], appel[2]).then(function (res) {
                            /* ⚠️ **UN APPEL QUI ÉCHOUE RESTE DANS LA FILE.** *C'est
                               la seule façon de ne pas perdre un geste de la
                               cliente à cause d'une seconde de réseau.* */
                            if (!res.ok) echoues.push(appel);
                        });
                    });
                })(aPousser[c]);
            }

            return chaine.then(function () {
                /* ── ③ COMPOSER ──────────────────────────────────────────────
                   *Une union, et non un remplacement : les favoris faits sur un
                   AUTRE APPAREIL arrivent par `duServeur`, ceux faits ici
                   arrivent par la file — **et aucun des deux n'écrase l'autre.*** */
                var union = duServeur.slice(0);
                for (var k = 0; k < e.aAjouter.length; k++) {
                    if (union.indexOf(e.aAjouter[k]) === -1) union.push(e.aAjouter[k]);
                }
                for (var m = 0; m < e.aRetirer.length; m++) {
                    var pos = union.indexOf(e.aRetirer[m]);
                    if (pos !== -1) union.splice(pos, 1);
                }

                /* ── ④ CE QUI N'A PAS PU PARTIR REPART AVEC LA SUIVANTE ──── */
                var resteAjouter = [], resteRetirer = [];
                for (var x = 0; x < echoues.length; x++) {
                    if (echoues[x][0] === "") resteAjouter.push(echoues[x][2].produit);
                    else resteRetirer.push(echoues[x][2].produit);
                }

                var nouvel = {
                    version: 2,
                    liste: union,
                    aAjouter: listePropre(resteAjouter).slice(0, MAX_ATTENTE),
                    aRetirer: listePropre(resteRetirer).slice(0, MAX_ATTENTE),
                    sync: Date.now(),
                };
                ecrireEtat(nouvel);
                RAISON_SYNC = "ACCORD";
                enCours = false;
                resituer();
                return {
                    ok: true, connecte: true,
                    serveur: duServeur.length,
                    pousses: aPousser.length - echoues.length,
                    restants: echoues.length,
                    total: union.length,
                };
            });
        });
    }

    /* ══════════════════════════════════════════════════════════════════════
       LE TITRE DU PRODUIT — *pour que l'annonce soit utile, pas générique.*
       🔑 Un lecteur d'écran qui entend « Ajouter aux favoris », **huit fois de
       suite sur une grille de huit produits**, ne peut pas savoir lequel.
       ⚠️ **Best-effort assumé** : si rien n'est trouvé, on retombe sur
       l'annonce générique. *Ce n'est pas un échec, c'est un repli.*
       ══════════════════════════════════════════════════════════════════════ */
    function titreDe(li) {
        var img = li.querySelector("img[alt]");
        if (img && img.getAttribute("alt")) {
            var t = img.getAttribute("alt").trim();
            if (t.length > 2 && t.length < 120) return t;
        }
        var a = li.querySelector("a[title]");
        if (a && a.getAttribute("title")) {
            var t2 = a.getAttribute("title").trim();
            if (t2.length > 2 && t2.length < 120) return t2;
        }
        return "";
    }

    /* ══════════════════════════════════════════════════════════════════════
       ⛔⛔ **L'ANCRAGE — ET C'EST LA CORRECTION LA PLUS IMPORTANTE DU FICHIER.**
       ══════════════════════════════════════════════════════════════════════
       **CE QUI S'EST PASSÉ, PARCE QUE ÇA A COÛTÉ UNE CAPTURE D'ÉCRAN.**

       La v1 posait le cœur **dans le `li[data-product-id]`**, marqué
       `position: relative`, avec `top: 9px; right: 9px`.
       ➜ **13 contrôles sur 13 sont passés au vert.** *Et la capture d'écran a
         montré deux cœurs **au bord droit d'une ligne, à côté du prix**.*

       **POURQUOI LES CONTRÔLES N'ONT RIEN DIT :** la question posée était
       *« le cœur est-il dans sa carte ? »* — **et « sa carte », c'était le `li`,
         MESURÉ À 1176 × 392 px, c'est-à-dire TOUTE LA LARGEUR.**
       > 🔑 *Le cœur était bien dedans. **Il était au mauvais endroit dedans.***
       >> **Un contrôle qui demande « est-ce dans le contenant ? » ne peut pas
       >> répondre à « est-ce au bon endroit dans le contenant ? »**

       **LA MESURE (`22-…`) — et elle a révélé DEUX mises en page :**
       ```
         COLLECTION — LISTE, 1 par ligne
           LI            1176 px   ← ce que la v1 visait
            └ SQUAMA-ITEM 1176 px   overflow: hidden
               └ A          1176 px
                  └ DIV.item-image   375 px
                     └ DIV.media      375 px  position: relative  ← L'ANCRE
                        └ IMG         375 px

         ACCUEIL — GRILLE, 6 par ligne
           LI             196 px
             └ …          196 px
                └ DIV.media  170 px  position: relative  ← LA MÊME ANCRE
       ```
       ➜ **`DIV.media` sur les deux.** *Un ancrage sur la largeur du `li` n'aurait
         marché que dans un cas sur deux* — **et il aurait fallu deux règles pour
         deux mises en page.** 🎯 *Un ancrage sur la largeur de **l'image** ne
         dépend pas de la mise en page : il dépend du **visuel**.*

       🔑 **ET LA RÈGLE EST CALCULÉE, PAS CODÉE EN DUR :** *on remonte les parents
         de l'`<img>` et on prend **le premier dont la largeur égale celle de
         l'image ET qui peut porter un enfant absolu**.* **Aucun nom de classe
         n'est supposé** — *donc si le thème renomme `.media`, ça continue de
         marcher.* ⛔ *Un sélecteur codé en dur `.media` aurait été le prochain
         piège.*
       ══════════════════════════════════════════════════════════════════════ */
    function ancrage(li) {
        var img = li.querySelector("img");
        /* ⛔ PAS DE VISUEL, PAS DE CŒUR. *On ne retombe PAS sur le `li` : ce
           serait reproduire exactement le défaut qu'on vient de corriger.* */
        if (!img) return null;

        var r;
        try { r = img.getBoundingClientRect(); } catch (e) { return null; }
        /* ⚠️ UNE IMAGE NON ENCORE DIMENSIONNÉE EST UN CAS RÉEL — *chargement
           paresseux, `squama-item` qui remplit après coup.* **Rendre `null`
           laisse la carte NON MARQUÉE, donc le balayage suivant réessaiera.** */
        if (!r || !r.width) return null;

        var n = img.parentElement;
        for (var k = 0; k < 6 && n && n !== li; k++) {
            var rn = null, cssN = null;
            try {
                rn = n.getBoundingClientRect();
                cssN = window.getComputedStyle(n);
            } catch (e) { /* nœud non mesurable — on continue de remonter */ }
            if (cssN && rn) {
                /* ── LES DEUX CONDITIONS, ET CHACUNE CORRIGE UN DÉFAUT ──────
                   ① **la largeur du visuel** ⇒ *l'ancre est le cadre de l'image,
                      pas la ligne qui la contient.*
                   ② **`position !== "static"`** ⇒ *sans ça, l'enfant absolu
                      s'échapperait jusqu'au premier ancêtre positionné — **qui
                      serait le `li`, c'est-à-dire le défaut d'origine.*** */
                if (cssN.position !== "static" && Math.abs(rn.width - r.width) <= 8) return n;
            }
            n = n.parentElement;
        }
        return null;
    }

    /* ⚠️ COMPTÉ, ET DIFFUSÉ — *une carte sans ancre est une information, pas un
       silence.* **Un cœur manquant qu'on ne compte pas est un cœur qu'on
       cherchera dans six mois.** */
    var SANS_ANCRE = 0;

    /* ══════════════════════════════════════════════════════════════════════
       LE CLIC — *et il ne fait plus qu'une chose de plus qu'avant : prévenir.*
       🔑 **L'INTERFACE N'ATTEND JAMAIS LE SERVEUR.** *`liste` est mise à jour
       immédiatement, le cœur se remplit, et la requête part derrière.*
       ⛔ **Un cœur qui attendrait 300 ms avant de se remplir serait perçu comme
       cassé** — *et c'est le seul retour que la cliente reçoit.* */
    function basculer(b, li) {
        var id = normaliser(li.getAttribute("data-product-id"));
        var titre = titreDe(li);
        var e = lireEtat();
        var actif = e.liste.indexOf(id) !== -1;
        var nouveau = !actif;

        if (actif) {
            var p = e.liste.indexOf(id);
            if (p !== -1) e.liste.splice(p, 1);
            /* ⛔ **UN AJOUT ANNULÉ N'EST PAS UN RETRAIT.** *Si l'ajout n'était
               jamais parti, le retirer du serveur serait une requête pour rien
               — et sur un produit jamais ajouté, elle retirerait le favori d'un
               AUTRE appareil.* ➜ *On annule l'ajout en attente ; on ne planifie
               un retrait que si un accord serveur a déjà eu lieu.* */
            var pa = e.aAjouter.indexOf(id);
            if (pa !== -1) e.aAjouter.splice(pa, 1);
            else if (e.sync !== 0 && e.aRetirer.indexOf(id) === -1) e.aRetirer.push(id);
        } else {
            var pr = e.aRetirer.indexOf(id);
            if (pr !== -1) e.aRetirer.splice(pr, 1);
            if (e.aAjouter.indexOf(id) === -1) e.aAjouter.push(id);
            if (e.liste.indexOf(id) === -1) e.liste.push(id);
        }

        var ok = ecrireEtat(e);

        /* ⚠️ **SI LE STOCKAGE A ÉCHOUÉ, ON NE MENT PAS À L'ÉCRAN.**
           *Remplir le cœur alors que rien n'a été retenu ferait croire à
           une mémoire qui n'existe pas.* */
        if (!ok) {
            b.setAttribute("data-etat", "erreur");
            poserEtat(b, false, titre);
            return;
        }

        b.removeAttribute("data-etat");
        poserEtat(b, nouveau, titre);
        battre(b);

        /* ⛔ **ON PRÉVIENT LE SERVEUR SEULEMENT SI ON SAIT QUI ON EST.**
           *`HINT === false` = anonyme déclaré par le layout → **aucune requête**,
           la file d'attente partira à la connexion.*
           *`HINT === null` = on ne sait pas → on tente : le pire est un `401`,
           qui renseigne sans rien écrire.* */
        if (HINT !== false) {
            b.setAttribute("data-etat", "attente");
            appeler(nouveau ? "" : "/remove", "POST", { produit: id }).then(function (r) {
                /* ⛔ **L'ATTRIBUT SE RETIRE DANS TOUS LES CAS.** *Le laisser après
                   un échec griserait le cœur pour toute la visite.* */
                b.removeAttribute("data-etat");
                if (r.ok && r.connecte) {
                    /* ✅ Le serveur a pris l'opération : elle sort de la file. */
                    var ee = lireEtat();
                    var file = nouveau ? ee.aAjouter : ee.aRetirer;
                    var q = file.indexOf(id);
                    if (q !== -1) file.splice(q, 1);
                    ee.sync = Date.now();
                    ecrireEtat(ee);
                    RAISON_SYNC = "ACCORD";
                } else if (r.ok && !r.connecte) {
                    /* *Anonyme confirmé par le serveur (401).* **La file d'attente
                       RESTE — c'est elle qui partira à la connexion** (§9-§12). */
                    HINT = false;
                    RAISON_SYNC = "ANONYME";
                    ecrireEtat(lireEtat());
                }
                /* ⚠️ `!r.ok` : **on ne touche à RIEN.** *L'opération reste dans la
                   file, et repartira à la prochaine synchronisation.* */
            });
        }

        /* ⛔ **UN ÉVÉNEMENT POUR LA SUITE.** *Pixels, mesure, ou barre de compte :
           ils écoutent, et ce fichier ne change pas.* */
        try {
            window.dispatchEvent(new CustomEvent("mm:favori", {
                detail: { produit: id, favori: nouveau, total: e.liste.length },
            }));
        } catch (err) {
            /* ⚠️ `CustomEvent` n'existe pas dans les très vieux moteurs —
               *et ce n'est pas une raison d'interrompre le clic.* */
        }
    }

    /* ══════════════════════════════════════════════════════════════════════
       POSER UN CŒUR SUR UNE CARTE
       ══════════════════════════════════════════════════════════════════════ */
    function poser(li) {
        if (!li || li.getAttribute("data-mm-pose") === "1") return false;

        var id = normaliser(li.getAttribute("data-product-id"));
        if (!id) return false;

        /* ⛔ UNE CARTE IMBRIQUÉE DANS UNE AUTRE : *on ne pose que sur la plus
           profonde.* **Sans ce test, une grille imbriquée porterait deux cœurs
           superposés, et le clic partirait au mauvais produit.** */
        if (li.querySelector(SELECTEUR_ANCRE)) return false;

        /* ⛔ ET SI LE THÈME A DÉJÀ SON PROPRE CŒUR, ON NE DOUBLONNE PAS. */
        if (li.querySelector("[data-mm-coeur]")) return false;

        /* ── ⛔ ON CHERCHE L'ANCRE AVANT TOUT LE RESTE ─────────────────────── */
        var hote = ancrage(li);
        if (!hote) {
            /* *On ne pose RIEN. La carte reste non marquée, donc réessayable.* */
            SANS_ANCRE += 1;
            return false;
        }

        var titre = titreDe(li);

        var b = document.createElement("button");
        b.type = "button";
        b.className = "mm-coeur";
        b.setAttribute("data-mm-coeur", VERSION);
        b.setAttribute("data-mm-produit", id);
        b.innerHTML = SVG;

        /* ⚠️ `mm-coeur-hote` EST UN REPÈRE, PAS UNE RÈGLE DE POSITION.
           ⛔ **Le CSS ne pose AUCUN `position` sur cette classe** — *sinon il
             écraserait un `position: absolute` déjà en place, et **casserait la
             mise en page du thème.*** ➜ *L'ancre est garantie non-statique par
             `ancrage()`, c'est tout ce qu'il faut.* */
        hote.classList.add(CLASSE_HOTE);
        li.setAttribute("data-mm-pose", "1");

        poserEtat(b, estFavori(id), titre);

        b.addEventListener("click", function (evt) {
            /* ⛔ **LES TROIS ARRÊTS, ET CHACUN CORRIGE UN DÉFAUT RÉEL :**
               ① `preventDefault` — *la carte est souvent enveloppée dans un
                  lien : sans lui, le clic navigue vers la fiche.*
               ② `stopPropagation` — *le thème écoute le clic sur la carte
                  (ajout rapide, aperçu). **Cocher un favori ne doit pas
                  déclencher un ajout au panier.**
               ③ `stopImmediatePropagation` — *le même événement peut porter
                  plusieurs écouteurs sur le même nœud ; le dernier posé
                  gagnerait sinon.* */
            evt.preventDefault();
            evt.stopPropagation();
            if (typeof evt.stopImmediatePropagation === "function") evt.stopImmediatePropagation();

            basculer(b, li);
        });

        /* ⛔ **ON AJOUTE À L'ANCRE DU VISUEL, PAS À LA CARTE.**
           *`appendChild` place le bouton comme dernier enfant du cadre de
           l'image ; c'est `position: absolute` qui le remonte dans le coin.
           **Aucune structure interne du cadre n'est supposée** — ce qui est
           exactement ce qu'il faut quand trois implémentations de carte
           coexistent.* */
        hote.appendChild(b);
        return true;
    }

    /* ══════════════════════════════════════════════════════════════════════
       BALAYER — *et la garde contre la boucle infinie.*
       🔑 **`poser()` marque la carte (`data-mm-pose`) AVANT que l'observateur
       ne revoie la mutation.** ➜ *L'observateur se déclenche bien, mais chaque
       carte est déjà marquée : la seconde passe ne fait rien et s'arrête.*
       **Sans ce marqueur, poser un bouton déclencherait une mutation, qui
       déclencherait un balayage, qui poserait un bouton — indéfiniment.**
       ══════════════════════════════════════════════════════════════════════ */
    var POSES = 0;

    function balayer(racine) {
        var zone = racine || document;
        var cartes;
        try {
            cartes = zone.querySelectorAll(SELECTEUR_ANCRE);
        } catch (e) {
            return 0;
        }
        var n = 0;
        for (var i = 0; i < cartes.length; i++) {
            if (poser(cartes[i])) n++;
        }
        POSES += n;
        return n;
    }

    /* ⛔ **RESITUER — ET ELLE SERT DEUX FOIS, PAS UNE.**
       *① après une synchronisation, pour que l'écran reflète ce que le serveur
         a rendu ;* *② après tout changement d'état venu d'ailleurs.*
       🔑 **Elle est séparée de `poser()` exprès** : *reposer les cœurs
       recréerait les boutons, donc perdrait le focus clavier — **une cliente au
       clavier perdrait sa position à chaque synchronisation.*** */
    function resituer() {
        var cartes = document.querySelectorAll(SELECTEUR_ANCRE);
        for (var i = 0; i < cartes.length; i++) {
            var b = cartes[i].querySelector("[data-mm-coeur]");
            if (b) poserEtat(b, estFavori(cartes[i].getAttribute("data-product-id")), titreDe(cartes[i]));
        }
    }

    /* ══════════════════════════════════════════════════════════════════════
       LA MISE EN ROUTE
       ══════════════════════════════════════════════════════════════════════ */
    function demarrer() {
        balayer(document);

        /* ── LES CARTES QUI ARRIVENT APRÈS LE CHARGEMENT ───────────────────
           ⚠️ **Elles arrivent vraiment** : le thème pagine sans recharger, la
           recherche prédictive injecte des cartes, et `<squama-item>` remplit
           les siennes **après** coup — *c'est même la raison d'être de ce
           fichier.* ➜ **Un balayage unique au chargement ne verrait qu'une
           partie des cartes**, et *les cœurs manquants seraient attribués à un
           bug du cœur, pas à un défaut d'observation.* */
        if (typeof MutationObserver === "function" && document.body) {
            var enAttente = false;
            var obs = new MutationObserver(function () {
                /* ⛔ ON REGROUPE. *Sans ceci, une grille de 40 cartes déclenche 40
                   balayages complets — chacun parcourant tout le document.* */
                if (enAttente) return;
                enAttente = true;
                window.requestAnimationFrame(function () {
                    enAttente = false;
                    balayer(document);
                });
            });
            obs.observe(document.body, { childList: true, subtree: true });
        }

        /* ── 🔑 LA SEULE REQUÊTE DE LA PAGE ────────────────────────────────
           ⛔ **APRÈS LE PREMIER BALAYAGE, ET C'EST IMPORTANT.** *L'interface est
           déjà juste grâce au miroir local ; la requête ne fait que la
           **corriger**.* ➜ *Si le réseau met 3 secondes, personne ne voit un
           cœur vide pendant 3 secondes : on voit l'état local, puis il s'affine.*
           🔑 *C'est la différence entre « rapide » et « qui a l'air rapide ».* */
        if (HINT !== false) {
            synchroniser().then(function (r) {
                try {
                    console.log("[mm-wishlist] " + VERSION + " · " + POSES + " carte(s) · "
                        + SANS_ANCRE + " sans ancre · " + REQUETES + " requête(s) · "
                        + (r && r.ok ? (r.connecte ? "connecté" : "anonyme") : "réseau indéterminé")
                        + (r && r.pousses ? " · " + r.pousses + " en attente poussée(s)" : ""));
                } catch (e) { /* la console peut être absente — aucun impact */ }
            });
        } else {
            /* ⛔ **ANONYME : LA FILE DE SESSION EST QUAND MÊME ÉCRITE.**
               *C'est §9-§12 : ce qui a été coché sans compte doit être prêt à
               partir.* **Si on ne l'écrivait qu'au clic, une visiteuse qui
               arrive avec une file (retour de connexion) la perdrait.** */
            ecrireEtat(lireEtat());
            try {
                console.log("[mm-wishlist] " + VERSION + " · " + POSES + " carte(s) · "
                    + SANS_ANCRE + " sans ancre · 0 requête (anonyme)");
            } catch (e) { /* idem */ }
        }
    }

    /* ══════════════════════════════════════════════════════════════════════
       L'API EXPOSÉE — *petite, et c'est pour vérifier, pas pour décorer.*
       🔑 **Un test ne doit pas aller fouiller `localStorage` en devinant le
       format.** ➜ `window.mmWishlist.compter()` est la porte d'entrée, et
       c'est elle que la sonde de `19-verifier-sur-le-site.mjs` appellera.
       ══════════════════════════════════════════════════════════════════════ */
    window.mmWishlist = {
        version: VERSION,
        cle: CLE,
        lire: function () { return lireEtat().liste; },
        estFavori: estFavori,
        compter: function () { return lireEtat().liste.length; },
        vider: function () {
            var ok = ecrireEtat(etatVide());
            resituer();
            return ok;
        },
        resituer: resituer,
        /* *Exposé pour que la sonde puisse MESURER le nombre de cartes vues,
           pas seulement le nombre de cœurs posés.* */
        cartesVues: function () { return document.querySelectorAll(SELECTEUR_ANCRE).length; },
        posees: function () { return POSES; },
        /* ⛔ **CARTES SANS ANCRE — et c'est une mesure, pas un détail.**
           *Si ce compteur monte, c'est que le thème a changé de structure :
           **une image sans cadre positionné ne peut plus porter de cœur.***
           ➜ *Un cœur manquant qu'on ne compte pas est un cœur qu'on cherchera
             dans six mois sans savoir où il a disparu.* */
        sansAncre: function () { return SANS_ANCRE; },

        /* ── LES QUATRE PORTES DE MESURE DE L'ÉTAPE 2 ─────────────────────
           🔑 **Sans elles, « une requête par page » et « le serveur est la
           source de vérité » resteraient des affirmations.** ➜ *Ce sont des
           mesures, et elles se lisent dans le navigateur.* */
        requetes: function () { return REQUETES; },
        client: function () { return HINT; },
        sync: synchroniser,
        etat: function () {
            var e = lireEtat();
            var m = magasin();
            return {
                version: e.version,
                client: HINT,
                raisonSync: RAISON_SYNC,
                liste: e.liste.slice(0),
                aAjouter: e.aAjouter.slice(0),
                aRetirer: e.aRetirer.slice(0),
                sync: e.sync,
                magasin: !m ? "aucun" : (m === window.localStorage ? "localStorage" : "sessionStorage"),
            };
        },
    };

    /* ── LE POINT D'ENTRÉE ─────────────────────────────────────────────────
       ⚠️ `defer` garantit que le document est analysé — **mais il ne garantit
       pas que `<squama-item>` ait fini son rendu.** ➜ On balaie au chargement,
       **et l'observateur prend le relais pour tout ce qui arrive ensuite.** */
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", demarrer, { once: true });
    } else {
        demarrer();
    }
})();
